import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SelectDropdownComponent } from '@app/components/select-dropdown';
import { ToastService, ToastType } from '@app/components/toast';
import { ToggleComponent, ToggleHexColor } from '@app/components/toggle';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { Logger } from '@app/services/logger/logger.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { noEmptyObject } from '@app/utils';
import { TranslatePipe } from '@ngx-translate/core';
import {
  GympassFeatureConfig,
  GympassProductTier,
  GympassProductTierDisplay,
  GympassProductType,
  GympassSector,
  GympassSubsidy,
  GympassTier,
} from '@skyfit/core-data-library';

const DEFAULT_DISCOUNT_AMOUNT = 10;

@Component({
  selector: 'app-gympass-feature',
  standalone: true,
  imports: [
    CommonModule,
    ToggleComponent,
    SelectDropdownComponent,
    TranslatePipe,
  ],
  templateUrl: './gympass-feature.component.html',
  styleUrl: './gympass-feature.component.css',
})
export class GympassFeatureComponent implements OnInit {
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly translatorService = inject(TranslatorService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(Logger);

  gympassFeatureConfig = model({} as GympassFeatureConfig);
  readMode = input(false);
  defaultSubsidyNameSelected = signal(GympassSubsidy.Digital);
  defaultDiscountAmountSelected = signal(DEFAULT_DISCOUNT_AMOUNT);
  memberBuyupEnabled = signal(false);
  discountEnabled = signal(false);
  discountDollarAmount = signal(DEFAULT_DISCOUNT_AMOUNT);
  enrollmentFeeEnabled = signal(false);
  availableTiers = signal<GympassProductTier[] | null>(null);
  productTiers = signal([] as GympassProductTier[]);
  productDisplayTiers = signal([] as GympassProductTierDisplay[]);
  productDiscountAmounts: number[] = [];
  productSubsidyNames: string[] = [];
  productEnrollmentFees: GympassProductTier[] = [];
  ToggleHexColor = ToggleHexColor;

  private readonly getSortedTiers = (
    a: GympassProductTier,
    b: GympassProductTier
  ) => {
    const weights = {
      [GympassTier.None]: 0,
      [GympassTier.Digital]: 1,
      [GympassTier.Classic]: 2,
      [GympassTier.Standard]: 3,
      [GympassTier.Premium]: 4,
      [GympassTier.Elite]: 5,
    };
    return weights[a.tier] - weights[b.tier];
  };

  private getProductTierDiscountedAmount(
    discount: number,
    tierDollarAmount: number
  ): number {
    return discount && tierDollarAmount > 0
      ? (Math.round(tierDollarAmount * 100) - Math.round(discount * 100)) / 100
      : 0;
  }

  private getProductDisplayTiers(): GympassProductTierDisplay[] {
    const subsidy = this.defaultSubsidyNameSelected();
    return this.productTiers()
      .filter((t) => t.subsidy === subsidy)
      .sort(this.getSortedTiers)
      .map((productTier) => {
        const discount = this.discountDollarAmount();
        const dollarAmount = productTier.dollarAmount ?? 0;
        return {
          buyupEnabled: productTier.subsidy === subsidy,
          description: productTier.description,
          discount,
          discountedAmount: this.getProductTierDiscountedAmount(
            discount,
            dollarAmount
          ),
          dollarAmount,
          enabled:
            String(productTier.tier) === subsidy ||
            (productTier.subsidy === subsidy && this.memberBuyupEnabled()),
          name: productTier.name,
          productTier,
        };
      })
      .filter(
        (disp) =>
          disp.productTier.tier && disp.productTier.tier !== GympassTier.None
      );
  }

  private getProductSubsidyOptions(products: GympassProductTier[]) {
    return Array.from(new Set(products.map((p) => p.subsidy))).filter(Boolean);
  }

  private getProductDiscountAmounts(products: GympassProductTier[]) {
    return products
      .filter((p) => p.type === GympassProductType.discount)
      .map((p) => p.dollarAmount);
  }

  ngOnInit() {
    this.adminTenantService
      .getTenantProducts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (products) => {
          this.productTiers.set(products.filter((p) => p.type === 'TIER'));
          this.productDisplayTiers.set(this.getProductDisplayTiers());

          this.productSubsidyNames = this.getProductSubsidyOptions(products);
          this.productDiscountAmounts =
            this.getProductDiscountAmounts(products);
          this.productEnrollmentFees = products.filter(
            (p) => p.type === GympassProductType.enrollmentFee
          );

          const initialDiscount = Number(
            this.gympassFeatureConfig()?.discount ?? DEFAULT_DISCOUNT_AMOUNT
          );
          this.discountDollarAmount.set(initialDiscount);
          this.defaultDiscountAmountSelected.set(initialDiscount);

          this.defaultSubsidyNameSelected.set(
            this.gympassFeatureConfig()?.subsidy ?? GympassSubsidy.Digital
          );
          this.enrollmentFeeEnabled.set(
            this.gympassFeatureConfig()?.enrollmentFee?.enabled ?? false
          );
          this.memberBuyupEnabled.set(
            this.gympassFeatureConfig()?.memberBuyUp ?? false
          );
          this.discountEnabled.set(!!this.gympassFeatureConfig()?.discount);

          this.updateGympassFeatureConfig();
        },
        error: (error) => {
          this.logger.warn(error);
          const msg = this.translatorService.translate(
            'tenantConfiguration.general.getProductsError'
          );
          this.toastService.add(msg, ToastType.ERROR);
        },
      });
  }

  private getDefaultTierForSubsidy(): GympassTier {
    const tier = this.productTiers().find(
      (t) =>
        t.subsidy === this.defaultSubsidyNameSelected() && t.dollarAmount === 0
    );
    return tier?.tier;
  }

  private getAvailableUserBuyupTiers(): GympassProductTier[] | null {
    if (!this.memberBuyupEnabled()) return null;
    return this.productTiers()
      .filter((t) => t.subsidy === this.defaultSubsidyNameSelected())
      .sort((a, b) => a.dollarAmount - b.dollarAmount);
  }

  private updateGympassFeatureConfig(): void {
    if (this.memberBuyupEnabled()) {
      if (this.discountEnabled()) {
        this.discountDollarAmount.set(this.defaultDiscountAmountSelected());
      }

      this.availableTiers.set(this.getAvailableUserBuyupTiers());
      this.gympassFeatureConfig.set(
        noEmptyObject({
          defaultTier: this.getDefaultTierForSubsidy() ?? GympassTier.Classic,
          sector:
            this.gympassFeatureConfig()?.sector ?? GympassSector.Commercial,
          subsidy: this.defaultSubsidyNameSelected(),
          memberBuyUp: true,
          availableTiers: this.availableTiers(),
          ...(this.discountEnabled() && {
            discount: this.discountDollarAmount(),
          }),
          enrollmentFee:
            this.enrollmentFeeEnabled() && this.productEnrollmentFees.length > 0
              ? { ...this.productEnrollmentFees[0], enabled: true }
              : {},
        })
      );
    } else {
      this.availableTiers.set([]);
      this.discountEnabled.set(false);
      this.discountDollarAmount.set(DEFAULT_DISCOUNT_AMOUNT);
      this.enrollmentFeeEnabled.set(false);

      this.gympassFeatureConfig.set({
        defaultTier: this.getDefaultTierForSubsidy() ?? GympassTier.Classic,
        sector: this.gympassFeatureConfig()?.sector ?? GympassSector.Commercial,
        subsidy: this.defaultSubsidyNameSelected() ?? GympassSubsidy.Classic,
        memberBuyUp: false,
      });
    }
    this.productDisplayTiers.set(this.getProductDisplayTiers());
  }

  handleDefaultSubsidy(value: GympassSubsidy) {
    this.defaultSubsidyNameSelected.set(value);
    this.updateGympassFeatureConfig();
  }

  handleMemberBuyup() {
    this.memberBuyupEnabled.update((s) => !s);
    this.updateGympassFeatureConfig();
  }

  handleEnrollmentFee() {
    this.enrollmentFeeEnabled.update((s) => !s);
    this.updateGympassFeatureConfig();
  }

  handleDiscount() {
    this.discountEnabled.update((s) => !s);
    this.updateGympassFeatureConfig();
  }

  handleDiscountAmount(value: number) {
    this.discountDollarAmount.set(Number(value));
    this.updateGympassFeatureConfig();
  }
}
